import React, { useState, useEffect } from 'react';

function ModernFilter({ grupos, onFilterChange, isMobile = false }) {
  // Estado para controlar a expansão/contração
  const [activeFilters, setActiveFilters] = useState({});
  const [tempFilters, setTempFilters] = useState({}); // Filtros temporários para mobile
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({}); // Grupos expandidos

  // Inicializar todos os grupos como expandidos
  useEffect(() => {
    if (!grupos || grupos.length === 0) {
      setExpandedGroups({});
      return;
    }

    setExpandedGroups(prev => {
      const updated = { ...prev };
      let changed = false;

      grupos.forEach((grupo, index) => {
        const key = grupo?.nome || `grupo-${index}`;
        if (!(key in updated)) {
          updated[key] = true;
          changed = true;
        }
      });

      Object.keys(updated).forEach(key => {
        if (!grupos.some((grupo, index) => (grupo?.nome || `grupo-${index}`) === key)) {
          delete updated[key];
          changed = true;
        }
      });

      return changed ? updated : prev;
    });
  }, [grupos]);

  const handleFilterToggle = (filterName, filterValue, isTemp = false) => {
    const filtersToUpdate = isTemp ? tempFilters : activeFilters;
    let newFilters = { ...filtersToUpdate };
    
    // Tratamento especial para categorias (permite múltiplas seleções)
    if (filterName === 'Categoria' || grupos.some(grupo => grupo.nome === filterName)) {
      // Se já existe um array para essa categoria
      if (Array.isArray(newFilters[filterName])) {
        const currentArray = newFilters[filterName];
        const valueIndex = currentArray.indexOf(filterValue);
        
        if (valueIndex > -1) {
          // Remove se já estiver selecionado (toggle off)
          newFilters[filterName] = currentArray.filter(item => item !== filterValue);
          // Se o array ficar vazio, remove a propriedade
          if (newFilters[filterName].length === 0) {
            delete newFilters[filterName];
          }
        } else {
          // Adiciona se não estiver selecionado (toggle on)
          newFilters[filterName] = [...currentArray, filterValue];
        }
      } else if (newFilters[filterName] === filterValue) {
        // Se é valor único e é igual ao atual, remove (toggle off)
        delete newFilters[filterName];
      } else if (newFilters[filterName]) {
        // Se já existe um valor diferente, transforma em array
        newFilters[filterName] = [newFilters[filterName], filterValue];
      } else {
        // Se não existe, adiciona como array com um item
        newFilters[filterName] = [filterValue];
      }
    } else {
      // Para filtros rápidos (boolean toggles)
      if (typeof filterValue === 'boolean') {
        if (newFilters[filterName] === filterValue) {
          // Se já está ativo, remove (toggle off)
          delete newFilters[filterName];
        } else {
          // Se não está ativo ou é diferente, ativa (toggle on)
          newFilters[filterName] = filterValue;
        }
      } else {
        // Para outros tipos de filtro
        if (newFilters[filterName] === filterValue) {
          delete newFilters[filterName];
        } else {
          newFilters[filterName] = filterValue;
        }
      }
    }
    
    if (isTemp) {
      setTempFilters(newFilters);
    } else {
      setActiveFilters(newFilters);
      onFilterChange(newFilters);
    }
  };

  const applyMobileFilters = () => {
    setActiveFilters(tempFilters);
    onFilterChange(tempFilters);
    setShowOffcanvas(false);
  };

  const toggleGroupExpansion = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const clearAllFilters = (isTemp = false) => {
    if (isTemp) {
      setTempFilters({});
    } else {
      setActiveFilters({});
      onFilterChange({});
    }
  };

  // Calcular número de filtros ativos
  const activeFiltersCount = Object.keys(activeFilters).reduce((count, key) => {
    const value = activeFilters[key];
    if (Array.isArray(value)) {
      return count + value.length;
    } else if (value) {
      return count + 1;
    }
    return count;
  }, 0);

  const mobileFiltersLabel = activeFiltersCount > 0
    ? `Filtrar Produtos (${activeFiltersCount})`
    : 'Filtrar Produtos';

  // Função para renderizar o conteúdo dos filtros
  const renderFilterContent = (filtersToUse, isTemp = false) => {
    const MAX_ITEMS_COLLAPSED = 5; // Mostrar apenas 5 itens quando colapsado

    return (
      <div
        className={`filter-content-scrollable modern-filter-content ${isMobile ? 'modern-filter-content-mobile' : 'modern-filter-content-desktop'}`}
      >
        {/* Botão limpar filtros */}
        {activeFiltersCount > 0 && (
          <div className="mb-3">
            <button
              className="btn btn-outline-danger btn-sm w-100"
              onClick={() => clearAllFilters(isTemp)}
            >
              <i className="bi bi-x-circle me-1"></i>
              Limpar todos os filtros
            </button>
          </div>
        )}

        {/* Lista de grupos */}
        {grupos && grupos.length > 0 ? (
          <div className="accordion" id="filterAccordion">
            {grupos.map((grupo, index) => {
              const groupKey = grupo?.nome || `grupo-${index}`;
              const groupActiveCount = Array.isArray(filtersToUse[grupo.nome])
                ? filtersToUse[grupo.nome].length
                : (filtersToUse[grupo.nome] ? 1 : 0);

              const isGroupExpanded = expandedGroups[groupKey] ?? true;
              const hasMany = grupo.valores && grupo.valores.length > MAX_ITEMS_COLLAPSED;
              const itemsToShow = (isGroupExpanded || !hasMany)
                ? grupo.valores
                : grupo.valores.slice(0, MAX_ITEMS_COLLAPSED);

              return (
                <div key={index} className="accordion-item modern-filter-accordion-item mb-2">
                  <h2 className="accordion-header">
                    <button
                      className={`accordion-button modern-filter-accordion-button ${isGroupExpanded ? '' : 'collapsed'}`}
                      type="button"
                      onClick={() => toggleGroupExpansion(groupKey)}
                    >
                      <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                        <span className="fw-bold">
                          {grupo.nome || `Grupo ${index + 1}`}
                        </span>
                        {groupActiveCount > 0 && (
                          <span className="badge bg-primary rounded-pill ms-2">
                            {groupActiveCount}
                          </span>
                        )}
                      </div>
                    </button>
                  </h2>
                  <div className={`accordion-collapse collapse ${isGroupExpanded ? 'show' : ''}`}>
                    <div className="accordion-body modern-filter-accordion-body">
                      {/* Itens do grupo */}
                      {itemsToShow && itemsToShow.map((valor, valorIndex) => {
                        const isActive = Array.isArray(filtersToUse[grupo.nome])
                          ? filtersToUse[grupo.nome].includes(valor)
                          : filtersToUse[grupo.nome] === valor;

                        return (
                          <div
                            key={valorIndex}
                            className={`filter-item ${isActive ? 'active' : ''}`}
                            onClick={() => handleFilterToggle(grupo.nome, valor, isTemp)}
                            role="button"
                            tabIndex={0}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span>{valor}</span>
                              {isActive && (
                                <i className="bi bi-check-circle-fill text-white"></i>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Botão "Ver mais/menos" */}
                      {hasMany && (
                        <button
                          className="btn btn-link btn-sm p-0 mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleGroupExpansion(groupKey);
                          }}
                        >
                          <i className={`bi bi-chevron-${isGroupExpanded ? 'up' : 'down'} me-1`}></i>
                          {isGroupExpanded ? 'Ver menos' : `Ver mais ${grupo.valores.length - MAX_ITEMS_COLLAPSED} itens`}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-muted py-4">
            <i className="bi bi-inbox fs-1 d-block mb-2"></i>
            <p>Nenhum filtro disponível</p>
          </div>
        )}

       
      </div>
    );
  };

  // Versão mobile com offcanvas
  if (isMobile) {
    return (
      <>
        {/* Botão flutuante para abrir filtros */}
        <button
          type="button"
          className="modern-filter-mobile-trigger w-100 position-relative"
          onClick={() => {
            setTempFilters(activeFilters);
            setShowOffcanvas(true);
          }}
        >
          <i className="bi bi-funnel me-2"></i>
          <span className="modern-filter-mobile-label">{mobileFiltersLabel}</span>
        </button>

        {/* Offcanvas */}
        <div className={`offcanvas offcanvas-end modern-filter-offcanvas ${showOffcanvas ? 'show' : ''}`}
             tabIndex="-1"
             style={{
               visibility: showOffcanvas ? 'visible' : 'hidden',
               width: '85%',
               maxWidth: '400px'
             }}>
          <div className="offcanvas-header modern-filter-offcanvas-header">
            <h5 className="offcanvas-title">
              <i className="bi bi-funnel me-2"></i>
              Filtros
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowOffcanvas(false)}
            ></button>
          </div>
          <div className="offcanvas-body modern-filter-offcanvas-body" style={{ overflowY: 'auto' }}>
            {renderFilterContent(tempFilters, true)}
          </div>
          <div className="offcanvas-footer modern-filter-offcanvas-footer">
            <div className="d-flex gap-2 w-100">
              <button
                className="btn btn-outline-secondary flex-fill"
                onClick={() => {
                  setTempFilters({});
                }}
              >
                Limpar
              </button>
              <button
                className="btn btn-modern flex-fill"
                onClick={applyMobileFilters}
              >
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Backdrop */}
        {showOffcanvas && (
          <div
            className="offcanvas-backdrop fade show"
            onClick={() => setShowOffcanvas(false)}
          ></div>
        )}
      </>
    );
  }

  // Versão desktop com scroll
  return (
    <div className="modern-card modern-filter-card mb-4">
      {/* Header do filtro - sempre visível */}
      <div className="modern-filter-header">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <i className="bi bi-funnel me-2 text-primary"></i>
            <h6 className="mb-0 fw-bold">Filtros</h6>
            {activeFiltersCount > 0 && (
              <span className="badge bg-primary ms-2">
                {activeFiltersCount}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Conteúdo scrollável dos filtros */}
      {renderFilterContent(activeFilters, false)}
    </div>
  );
}

export default ModernFilter;
